import { type ReactElement, useMemo } from 'react'
import { isAxiosError } from 'axios'
import { useForm } from 'react-hook-form'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  useCreateCatalogComponent,
  useListCatalogSuppliers,
} from '@/shared/hooks/api/catalog'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

type V2CreateComponentFormValues = {
  supplierId: string
  sku: string
  name: string
}

type V2CreateComponentDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated?: () => void
}

export function V2CreateComponentDialog({
  open,
  onOpenChange,
  onCreated,
}: V2CreateComponentDialogProps): ReactElement {
  const { t } = useTranslation('common')
  const { mutateAsync: createComponent, isPending } = useCreateCatalogComponent()
  const { data: suppliers = [], isLoading: isLoadingSuppliers } =
    useListCatalogSuppliers()

  const form = useForm<V2CreateComponentFormValues>({
    defaultValues: {
      supplierId: '',
      sku: '',
      name: '',
    },
  })

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      form.reset()
    }
    onOpenChange(nextOpen)
  }

  function validateSupplierId(value: string | undefined): true | string {
    if (value == null || value.trim().length === 0) {
      return t('v2.pages.components.create.validation.supplierRequired')
    }
    return true
  }

  function validateSku(value: string | undefined): true | string {
    if (value == null || value.trim().length === 0) {
      return t('v2.pages.components.create.validation.skuRequired')
    }
    return true
  }

  async function handleSubmit(values: V2CreateComponentFormValues) {
    try {
      await createComponent({
        supplierId: values.supplierId,
        sku: values.sku.trim(),
        name:
          values.name.trim().length > 0 ? values.name.trim() : null,
      })
      form.reset()
      onOpenChange(false)
      onCreated?.()
    } catch (error: unknown) {
      if (isAxiosError(error) && error.response?.status === 409) {
        toast.error(t('v2.pages.components.create.errors.duplicateSku'))
        return
      }
      toast.error(t('v2.pages.components.create.errors.submitFailed'))
    }
  }

  const supplierItems = useMemo(
    () =>
      suppliers.map((supplier) => ({
        value: supplier.id,
        label: supplier.name,
      })),
    [suppliers]
  )

  const showNoSuppliers =
    !isLoadingSuppliers && suppliers.length === 0

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('v2.pages.components.create.title')}</DialogTitle>
          <DialogDescription>
            {t('v2.pages.components.create.description')}
          </DialogDescription>
        </DialogHeader>
        {showNoSuppliers ? (
          <p className="text-sm text-muted-foreground">
            {t('v2.pages.components.create.noSuppliers')}
          </p>
        ) : (
          <Form {...form}>
            <form
              className="space-y-4"
              onSubmit={form.handleSubmit(handleSubmit)}
            >
              <FormField
                control={form.control}
                name="supplierId"
                rules={{ validate: validateSupplierId }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('v2.pages.components.create.supplierLabel')}
                    </FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={isPending || isLoadingSuppliers}
                      items={supplierItems}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue
                            placeholder={t(
                              'v2.pages.components.create.supplierPlaceholder'
                            )}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {suppliers.map((supplier) => (
                          <SelectItem key={supplier.id} value={supplier.id}>
                            {supplier.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="sku"
                rules={{ validate: validateSku }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('v2.pages.components.create.skuLabel')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        autoComplete="off"
                        disabled={isPending}
                        placeholder={t(
                          'v2.pages.components.create.skuPlaceholder'
                        )}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('v2.pages.components.create.nameLabel')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        autoComplete="off"
                        disabled={isPending}
                        placeholder={t(
                          'v2.pages.components.create.namePlaceholder'
                        )}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isPending}
                  onClick={() => handleOpenChange(false)}
                >
                  {t('actions.cancel')}
                </Button>
                <Button type="submit" disabled={isPending || showNoSuppliers}>
                  {isPending && (
                    <Loader2 className="size-4 animate-spin" aria-hidden />
                  )}
                  {t('v2.pages.components.create.submit')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  )
}
